<template>
  <div class="more-actions-container" ref="container">
    <button class="button more-actions-trigger" @click.stop="toggleMenu" :disabled="disabled">
      <span class="dots">&#8226;&#8226;&#8226;</span>
    </button>
    <Teleport to="body">
      <Transition name="menu-slide">
        <div v-if="isOpen" class="more-actions-menu" :style="menuStyle">
          <div
            v-for="action in actions"
            :key="action.key"
            class="menu-button-wrapper"
            @mouseenter="showTooltip(action, $event)"
            @mouseleave="hideTooltip"
          >
            <button
              class="button menu-button"
              :class="{ 'danger-button': action.danger, 'disabled-button': action.disabled }"
              @click.stop="handleAction(action)"
              :disabled="action.disabled"
            >
              {{ action.label }}
            </button>
          </div>
        </div>
      </Transition>
      <Transition name="tooltip-fade">
        <div v-if="tooltipVisible && tooltipText" class="action-tooltip" :style="tooltipStyle">
          {{ tooltipText }}
        </div>
      </Transition>
    </Teleport>
  </div>
</template>

<script>
export default {
  name: 'MoreActionsMenu',
  props: {
    actions: {
      type: Array,
      required: true,
      // Each action: { key: string, label: string, danger?: boolean, disabled?: boolean, tooltip?: string }
    },
    disabled: {
      type: Boolean,
      default: false
    }
  },
  emits: ['action'],
  data() {
    return {
      isOpen: false,
      menuPosition: { top: 0, left: 0 },
      tooltipVisible: false,
      tooltipText: '',
      tooltipPosition: { top: 0, left: 0 }
    };
  },
  computed: {
    menuStyle() {
      return {
        top: `${this.menuPosition.top}px`,
        left: `${this.menuPosition.left}px`
      };
    },
    tooltipStyle() {
      return {
        top: `${this.tooltipPosition.top}px`,
        left: `${this.tooltipPosition.left}px`
      };
    }
  },
  mounted() {
    document.addEventListener('click', this.handleClickOutside);
  },
  beforeUnmount() {
    document.removeEventListener('click', this.handleClickOutside);
  },
  methods: {
    toggleMenu() {
      if (this.disabled) return;
      if (!this.isOpen) {
        this.calculatePosition();
      }
      this.isOpen = !this.isOpen;
    },
    calculatePosition() {
      const container = this.$refs.container;
      if (container) {
        const rect = container.getBoundingClientRect();
        this.menuPosition = {
          top: rect.bottom + 4,
          left: rect.right - 180 // menu width is ~180px, align right edge
        };
      }
    },
    handleClickOutside(event) {
      const container = this.$refs.container;
      const clickedContainer = container && container.contains(event.target);
      const clickedMenu = event.target.closest('.more-actions-menu');
      if (!clickedContainer && !clickedMenu) {
        this.isOpen = false;
      }
    },
    handleAction(action) {
      if (action.disabled) return;
      this.isOpen = false;
      this.$emit('action', action.key);
    },
    showTooltip(action, event) {
      if (action.disabled && action.tooltip) {
        const rect = event.target.getBoundingClientRect();
        this.tooltipText = action.tooltip;
        this.tooltipPosition = {
          top: rect.top + rect.height / 2,
          left: rect.left - 8
        };
        this.tooltipVisible = true;
      }
    },
    hideTooltip() {
      this.tooltipVisible = false;
      this.tooltipText = '';
    }
  }
}
</script>

<style scoped>
.more-actions-container {
  position: relative;
  display: inline-block;
}

.more-actions-trigger {
  min-width: 36px;
  height: 31px;
  padding: 4px 8px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.dots {
  font-size: 14px;
  letter-spacing: 2px;
  line-height: 1;
  color: #6b7280;
}

.more-actions-trigger:hover .dots {
  color: #333;
}

.more-actions-menu {
  position: fixed;
  z-index: 1000;
  background: white;
  border: 1px solid #e0e0e0;
  border-radius: var(--border-radius);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  min-width: 180px;
  overflow: hidden;
}

.menu-button {
  width: 100%;
  text-align: left;
  border-radius: var(--border-radius);
  border: none;
  box-shadow: none;
}

.menu-button:hover {
  box-shadow: none;
}

.menu-button:active {
  box-shadow: none;
}

.danger-button {
  color: #cf1322;
}

.danger-button:hover {
  background-color: #fff1f0;
  border-color: transparent;
}

.disabled-button {
  opacity: 0.5;
  cursor: not-allowed;
}

.menu-button-wrapper {
  position: relative;
}

.action-tooltip {
  position: fixed;
  z-index: 10001;
  background-color: #1f2937;
  color: #fff;
  padding: 6px 12px;
  border-radius: var(--border-radius);
  font-size: 13px;
  line-height: 1.4;
  max-width: 200px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  transform: translateX(-100%) translateY(-50%);
  white-space: nowrap;
}

/* Tooltip animation */
.tooltip-fade-enter-active,
.tooltip-fade-leave-active {
  transition: opacity 0.15s ease;
}

.tooltip-fade-enter-from,
.tooltip-fade-leave-to {
  opacity: 0;
}

/* Menu animation */
.menu-slide-enter-active {
  transition: all 0.2s ease-out;
}

.menu-slide-leave-active {
  transition: all 0.15s ease-in;
}

.menu-slide-enter-from {
  opacity: 0;
  transform: scale(0.95);
  transform-origin: top right;
}

.menu-slide-leave-to {
  opacity: 0;
  transform: scale(0.95);
  transform-origin: top right;
}
</style>
